/**
 * CompanyPoliciesModal Component
 * Displays company policies and allows employees to sign electronically
 */
import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { 
  FileText, CheckCircle, AlertTriangle, Loader2, 
  Shield, Clock, Users, Monitor, Share2, Scale, Gavel,
  ChevronRight, ChevronLeft
} from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';

const POLICY_ICONS = {
  attendance: Clock,
  conduct: Users,
  confidentiality: Shield,
  it_usage: Monitor,
  social_media: Share2,
  ethics: Scale,
  disciplinary: Gavel
};

const CompanyPoliciesModal = ({ isOpen, onClose, onSigned }) => {
  const [policies, setPolicies] = useState(null);
  const [loading, setLoading] = useState(true);
  const [signing, setSigning] = useState(false);
  const [signatureName, setSignatureName] = useState('');
  const [agreed, setAgreed] = useState(false);
  const [currentSection, setCurrentSection] = useState(0);
  const [readSections, setReadSections] = useState(new Set());

  useEffect(() => {
    if (isOpen) {
      fetchPolicies();
    }
  }, [isOpen]);

  const fetchPolicies = async () => {
    try {
      setLoading(true);
      const response = await api.get('/policies');
      setPolicies(response.data);
      if (response.data.has_signed) {
        setReadSections(new Set(response.data.policies.map((_, i) => i)));
      }
    } catch (error) {
      toast.error('Failed to load company policies');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleSignPolicies = async () => {
    if (!signatureName.trim()) {
      toast.error('Please enter your full name as signature');
      return;
    }
    if (!agreed) {
      toast.error('You must agree to the policies');
      return;
    }
    if (readSections.size < policies.policies.length) {
      toast.error('Please read all policy sections before signing');
      return;
    }

    try {
      setSigning(true);
      await api.post('/policies/sign', {
        full_name: signatureName,
        agreed: true,
        user_agent: navigator.userAgent
      });
      toast.success('Policies signed successfully!');
      if (onSigned) onSigned();
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to sign policies');
    } finally {
      setSigning(false);
    }
  };

  const markSectionRead = (index) => {
    setReadSections(prev => new Set([...prev, index]));
  };

  // Auto-mark section as read when viewing it
  useEffect(() => {
    if (policies && currentSection >= 0) {
      // Mark current section as read after a short delay (user has viewed it)
      const timer = setTimeout(() => {
        markSectionRead(currentSection);
      }, 1000); // Mark as read after 1 second of viewing
      return () => clearTimeout(timer);
    }
  }, [currentSection, policies]);

  const goToNextSection = () => {
    markSectionRead(currentSection);
    if (currentSection < policies.policies.length - 1) {
      setCurrentSection(currentSection + 1);
    }
  };

  const goToPrevSection = () => {
    if (currentSection > 0) {
      setCurrentSection(currentSection - 1);
    }
  };

  // Function to mark last section and enable signing
  const markLastSectionRead = () => {
    markSectionRead(currentSection);
  };

  if (loading) {
    return (
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className="max-w-4xl">
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-teal-600" />
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  if (!policies) return null;

  const currentPolicy = policies.policies[currentSection];
  const PolicyIcon = POLICY_ICONS[currentPolicy?.id] || FileText;
  const allSectionsRead = readSections.size >= policies.policies.length;
  const isLastSection = currentSection === policies.policies.length - 1;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col bg-white dark:bg-slate-800 p-0">
        {/* Header */}
        <DialogHeader className="p-5 pb-3 border-b border-gray-200 dark:border-slate-700 flex-shrink-0">
          <DialogTitle className="flex items-center gap-3 text-lg">
            <FileText className="w-5 h-5 text-teal-600" />
            H E Direct - Company Policies & Code of Conduct
            <Badge className="ml-2 bg-blue-100 text-blue-700 text-xs">v{policies.version}</Badge>
          </DialogTitle>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Effective: {policies.effective_date} • {policies.has_signed ? 'Signed' : 'Signature Required'}
          </p>
        </DialogHeader>

        {/* Progress Bar */}
        <div className="px-5 py-3 bg-gray-50 dark:bg-slate-900 border-b border-gray-200 dark:border-slate-700 flex-shrink-0">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
              Section {currentSection + 1} of {policies.policies.length}
            </span>
            <span className={`text-sm font-medium ${allSectionsRead ? 'text-green-600' : 'text-amber-600'}`}>
              {readSections.size}/{policies.policies.length} sections read
            </span>
          </div>
          <div className="flex gap-1">
            {policies.policies.map((_, index) => (
              <button
                key={index}
                onClick={() => { setCurrentSection(index); markSectionRead(index); }}
                className={`flex-1 h-2 rounded-full transition-colors ${
                  index === currentSection 
                    ? 'bg-teal-500' 
                    : readSections.has(index) 
                      ? 'bg-green-400' 
                      : 'bg-gray-300 dark:bg-slate-600'
                }`}
              />
            ))}
          </div>
        </div>

        {/* Policy Content - Main Scrollable Area */}
        <div className="flex-1 overflow-y-auto p-6">
          {/* Policy Title */}
          <div className="flex items-center gap-4 mb-6 pb-4 border-b border-gray-200 dark:border-slate-700">
            <div className="p-3 bg-teal-100 dark:bg-teal-900/30 rounded-xl">
              <PolicyIcon className="w-8 h-8 text-teal-600" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-800 dark:text-white">
                {currentPolicy?.title}
              </h2>
              <Badge className="mt-1 bg-gray-100 text-gray-600 dark:bg-slate-700 dark:text-gray-300">
                {currentPolicy?.category}
              </Badge>
            </div>
          </div>

          {/* Policy Content */}
          <div className="prose dark:prose-invert max-w-none mb-6">
            {currentPolicy?.content.split('\n').map((line, i) => {
              const trimmedLine = line.trim();
              if (!trimmedLine) return null;
              
              if (trimmedLine.startsWith('**') && trimmedLine.endsWith('**')) {
                return (
                  <h3 key={i} className="text-lg font-semibold text-gray-800 dark:text-white mt-6 mb-3 flex items-center gap-2">
                    <div className="w-1.5 h-6 bg-teal-500 rounded-full"></div>
                    {trimmedLine.replace(/\*\*/g, '')}
                  </h3>
                );
              }
              if (trimmedLine.startsWith('- ')) {
                return (
                  <div key={i} className="flex items-start gap-3 ml-4 mb-2">
                    <div className="w-2 h-2 bg-teal-500 rounded-full mt-2 flex-shrink-0"></div>
                    <p className="text-gray-700 dark:text-gray-300 text-base leading-relaxed">
                      {trimmedLine.substring(2)}
                    </p>
                  </div>
                );
              }
              return (
                <p key={i} className="text-gray-700 dark:text-gray-300 text-base leading-relaxed mb-3">
                  {trimmedLine}
                </p>
              );
            })}
          </div>

          {/* Navigation Buttons */}
          <div className="flex items-center justify-between pt-4 border-t border-gray-200 dark:border-slate-700">
            <Button
              variant="outline"
              onClick={goToPrevSection}
              disabled={currentSection === 0}
              className="gap-2"
            >
              <ChevronLeft className="w-4 h-4" />
              Previous
            </Button>
            
            {!isLastSection ? (
              <Button
                onClick={goToNextSection}
                className="gap-2 bg-teal-600 hover:bg-teal-700"
              >
                Next Section
                <ChevronRight className="w-4 h-4" />
              </Button>
            ) : (
              <Button
                onClick={markLastSectionRead}
                className={`gap-2 ${readSections.has(currentSection) 
                  ? 'bg-green-600 hover:bg-green-700' 
                  : 'bg-teal-600 hover:bg-teal-700'}`}
              >
                <CheckCircle className="w-4 h-4" />
                {readSections.has(currentSection) ? 'Section Complete' : 'Mark as Read'}
              </Button>
            )}
          </div>
        </div>

        {/* Signature Section - Fixed at Bottom */}
        {!policies.has_signed && (
          <div className="border-t-2 border-teal-500 p-5 bg-gradient-to-r from-gray-50 to-teal-50 dark:from-slate-900 dark:to-slate-800 flex-shrink-0">
            <h4 className="font-bold text-gray-800 dark:text-white mb-3 flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-500" />
              Employee Acknowledgment & Electronic Signature
            </h4>
            
            <div className="bg-white dark:bg-slate-800 rounded-lg p-4 border border-gray-200 dark:border-slate-700 mb-4 text-sm text-gray-600 dark:text-gray-300">
              By signing below, I acknowledge that I have read and understood all company policies. 
              I agree to comply with these policies as a condition of my employment. 
              Failure to comply may result in disciplinary action.
            </div>

            <div className="flex flex-wrap items-center gap-4">
              <div className="flex items-center gap-2">
                <Checkbox
                  id="agree"
                  checked={agreed}
                  onCheckedChange={setAgreed}
                  data-testid="policy-agree-checkbox"
                />
                <label htmlFor="agree" className="text-sm text-gray-700 dark:text-gray-300 cursor-pointer">
                  I agree to all policies
                </label>
              </div>
              
              <div className="flex-1 min-w-[200px]">
                <Input
                  value={signatureName}
                  onChange={(e) => setSignatureName(e.target.value)}
                  placeholder="Type your full name"
                  className="font-serif italic"
                  data-testid="policy-signature-input"
                />
              </div>
              
              <Button
                onClick={handleSignPolicies}
                disabled={signing || !agreed || !signatureName.trim() || !allSectionsRead}
                className="bg-teal-600 hover:bg-teal-700 gap-2"
                data-testid="sign-policies-btn"
              >
                {signing ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <CheckCircle className="w-4 h-4" />
                )}
                Sign & Submit
              </Button>
            </div>
            
            {!allSectionsRead && (
              <p className="text-sm text-amber-600 mt-3 flex items-center gap-2">
                <AlertTriangle className="w-4 h-4" />
                Please read all {policies.policies.length} policy sections before signing
              </p>
            )}
          </div>
        )}

        {/* Already Signed Message */}
        {policies.has_signed && (
          <div className="border-t-2 border-green-500 p-5 bg-green-50 dark:bg-green-900/20 flex-shrink-0">
            <div className="flex items-center gap-3">
              <CheckCircle className="w-6 h-6 text-green-600" />
              <div>
                <p className="font-medium text-green-800 dark:text-green-400">
                  You have signed this policy version
                </p>
                <p className="text-sm text-green-600 dark:text-green-500">
                  Signed on: {new Date(policies.signature_date).toLocaleDateString('en-US', {
                    year: 'numeric', month: 'long', day: 'numeric'
                  })}
                </p>
              </div>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default CompanyPoliciesModal;
